/**
 * Pushing a draft to the device.
 *
 * ORDER MATTERS, and it is not the obvious one. The sequence is:
 *
 *   0. STOP              park before changing any live configuration
 *   1. SET_CONFIG        header: kind, duration, generator params
 *   2. PATTERN_BEGIN     stage the steps (generators declare 0)
 *   3. PATTERN_DATA xN   stage, 9 steps per frame
 *   4. PATTERN_END       commit
 *   5. SAVE_TO_FLASH     optional, then await the async SAVE_RESULT
 *   6. START             optional
 *
 * SET_CONFIG goes first because the device derives `step_count` from Pattern, and
 * PATTERN_END writes it back — so the header lands first and the commit corrects
 * the count. Doing it the other way round leaves a window where the config
 * declares a step count the pattern has not got.
 *
 * The steps are STAGED, not applied: PATTERN_END is the only thing that touches the
 * live pattern, so a failure part-way through leaves the previous pattern intact.
 * That matters because the device may be running it right now.
 *
 * What actually protects against a half-applied config is not the ordering, though
 * — it is that SAVE_TO_FLASH only runs after every prior step succeeded. The device
 * on its own will happily run a mixture until the next push.
 */

import { MSG_STEPS_PER_FRAME, PatternKind } from '../../protocol/constants.ts';
import type { Transport } from '../../hid/transport.ts';
import { draftToDevice, type Draft } from '../../domain/draft.ts';
import type { JigStep, SaveResult } from '../../protocol/types.ts';

export interface StepChunk {
  offset: number;
  steps: JigStep[];
}

/**
 * What actually gets put on the wire.
 *
 * Split out from the transport calls so the rule below can be tested without a
 * device attached. It exists because of a shipped bug: `pushDraft` declared 0 steps
 * for a generator but still looped over the full step list, so every chunk exceeded
 * the declared count and the device answered `NACK BAD_STATE` (in reply to
 * PATTERN_DATA). It only happened when the kind was a generator AND the step list
 * was non-empty — which is easy to reach, because the step table deliberately
 * KEEPS the list when you switch kind so you do not lose it.
 *
 * The rule: a generator uploads no steps at all. Not "declares zero and sends the
 * stale list anyway" — none.
 */
export function planPatternUpload(
  kind: number,
  steps: readonly JigStep[],
): { kind: number; declaredSteps: number; chunks: StepChunk[] } {
  if (kind !== PatternKind.MACRO) {
    return { kind, declaredSteps: 0, chunks: [] };
  }

  const chunks: StepChunk[] = [];
  for (let offset = 0; offset < steps.length; offset += MSG_STEPS_PER_FRAME) {
    chunks.push({ offset, steps: steps.slice(offset, offset + MSG_STEPS_PER_FRAME) });
  }

  return { kind, declaredSteps: steps.length, chunks };
}

export interface PushOptions {
  /** Persist to flash. Without this a replug restores whatever was saved before. */
  save: boolean;
  /** Send START afterwards. Off by default: the app parks the device, on purpose. */
  startAfter?: boolean;
  /** Called before each stage, for a progress line. */
  onProgress?: (message: string) => void;
}

export interface PushResult {
  saved: boolean;
  saveResult?: SaveResult;
}

export async function pushDraft(
  transport: Transport,
  draft: Draft,
  options: PushOptions,
): Promise<PushResult> {
  const progress = options.onProgress ?? (() => undefined);
  const upload = planPatternUpload(draft.kind, draft.steps);

  progress('parking the device…');
  await transport.stop();

  progress('applying configuration…');
  await transport.setConfig(draftToDevice(draft));

  progress(
    upload.declaredSteps > 0
      ? `staging ${upload.declaredSteps} step${upload.declaredSteps === 1 ? '' : 's'}…`
      : 'staging generator…',
  );
  await transport.beginPattern(upload.kind, upload.declaredSteps);

  for (const chunk of upload.chunks) {
    progress(`uploading steps ${chunk.offset + 1}-${chunk.offset + chunk.steps.length} of ${upload.declaredSteps}…`);
    await transport.sendPatternChunk(chunk.offset, chunk.steps);
  }

  progress('committing…');
  await transport.endPattern();

  let saveResult: SaveResult | undefined;
  if (options.save) {
    progress('saving to flash…');
    saveResult = await transport.saveAndWait();
    if (!saveResult.ok) {
      throw new Error(
        `The device could not write to flash (HAL status ${saveResult.halStatus}). ` +
          'The configuration is live but will not survive a replug.',
      );
    }
  }

  if (options.startAfter) {
    progress('starting…');
    await transport.start();
  }

  progress('done');
  return { saved: options.save, ...(saveResult ? { saveResult } : {}) };
}
